import React, { useState, useEffect } from 'react';

// Small shared pieces for the Note Requests page, its Apply modal / Link
// panel and the Admin "Note Sync" tab: status + listing-state chips, the
// review-reason wording, the before → after diff renderer, and the same
// order-number normalization the backend matcher uses (so the "Will match:"
// hint under the input shows exactly what will be compared).

// Mirrors backend/services/noteSync/matching.js normalize(): uppercase, keep [A-Z0-9]
export function normalizeOrder(raw) {
  return String(raw ?? '').toUpperCase().replace(/[^A-Z0-9]/g, '');
}

// Client-side pre-check mirroring validateOrderNumber() so the buyer sees the
// problem before submitting; the server is still the authority.
export function orderNumberProblem(raw) {
  const trimmed = String(raw ?? '').trim();
  if (!trimmed) return 'Order number is required';
  if (trimmed.includes('@')) return 'That looks like an email address, not an order number';
  const norm = normalizeOrder(trimmed);
  if (norm.length < 6) return 'Too short — need at least 6 letters/digits';
  if (norm.length > 40) return 'Too long for an order number';
  if (!/[0-9]/.test(norm)) return 'Needs at least one digit';
  return null;
}

export const STATUS_LABELS = {
  waiting: 'Waiting',
  matched: 'Matched',
  needs_review: 'Needs review',
  done: 'Done',
  resolved: 'Resolved',
  cancelled: 'Cancelled',
  expired: 'Expired',
};

export const LISTING_STATE_LABELS = {
  pending: 'pending',
  applied: 'applied',
  verified: 'verified',
  skipped: 'skipped',
  failed: 'failed',
  gone: 'gone',
};

// Why a request sits in the staff queue (request.reviewReason)
export const REVIEW_REASON_LABELS = {
  substring_only: 'substring match — confirm',
  multi_event: 'matched 2+ events — confirm',
  too_many_matches: 'too many matches',
  partial_number: 'number matched without its suffix — confirm',
  free_text: 'free text — review wording',
  requires_review: 'needs review',
  held: 'submitter asked for a pricer check',
  write_failed: 'write failed ×3',
  too_long: 'public notes too long',
};

export const OPEN_STATUSES = ['waiting', 'matched', 'needs_review'];

export function StatusChip({ status }) {
  return (
    <span className={`nr-chip nr-status-${status || 'unknown'}`}>
      {STATUS_LABELS[status] || status || '—'}
    </span>
  );
}

export function ListingStateChip({ state }) {
  return (
    <span className={`nr-chip nr-state-${state || 'unknown'}`}>
      {LISTING_STATE_LABELS[state] || state || '—'}
    </span>
  );
}

// "Sec 114 Row 12" — the buyer-facing listing label
export function secRow(l) {
  const parts = [];
  if (l?.section) parts.push(`Sec ${l.section}`);
  if (l?.row) parts.push(`Row ${l.row}`);
  return parts.join(' ') || (l?.inventoryId ? `#${l.inventoryId}` : '—');
}

// Category labels + free text + tags, e.g. "Limited View, ADA" / "Other: “Aisle seat”" / "tags: 1test, 2test"
export function noteSummary(req) {
  const labels = (req?.categoryLabels && req.categoryLabels.length ? req.categoryLabels : req?.categories) || [];
  const parts = [...labels];
  if (req?.noteText) parts.push(`“${req.noteText}”`);
  if (req?.tags?.length) parts.push(`tags: ${req.tags.join(', ')}`);
  return parts.join(', ') || '—';
}

// Render `after` with the part appended to `before` highlighted. compose()
// trims trailing whitespace from the base then appends separator + phrases,
// so when `after` starts with the trimmed base the tail is exactly what was
// added. Falls back to plain text when the shapes don't line up.
export function AfterText({ before, after }) {
  const base = String(before ?? '').replace(/\s+$/, '');
  const a = String(after ?? '');
  if (a && base && a.startsWith(base) && a.length > base.length) {
    return (
      <span className="nr-diff-after">
        {base}
        <span className="nr-added">{a.slice(base.length)}</span>
      </span>
    );
  }
  if (a && !base) return <span className="nr-diff-after"><span className="nr-added">{a}</span></span>;
  return <span className="nr-diff-after">{a || <em className="nr-muted">(empty)</em>}</span>;
}

// "before → after" on one line (Done tab, recent writes, apply results)
export function NoteDiff({ before, after }) {
  return (
    <span className="nr-diff">
      <span className="nr-diff-before">{before ? before : <em className="nr-muted">(empty)</em>}</span>
      <span className="nr-diff-arrow"> → </span>
      <AfterText before={before} after={after} />
    </span>
  );
}

// Highlight every whitespace token of `text` whose normalized form contains
// `needle` (already normalized). When no single token contains it, highlight
// a contiguous run of tokens whose joined normalized form does — that is how
// compound vendor formats ("BRWV - 5274214 - 65663549") match on the server
// (the matcher's "phrase" tier). Used on internal notes in the Link panel.
const HIGHLIGHT_MAX_RUN = 6;

export function HighlightToken({ text, needle }) {
  const s = String(text ?? '');
  if (!s) return <em className="nr-muted">—</em>;
  const n = normalizeOrder(needle);
  if (!n) return <>{s}</>;
  const parts = s.split(/(\s+)/);          // words at even indices, whitespace at odd
  const norms = parts.map((p, i) => (i % 2 === 0 ? normalizeOrder(p) : ''));

  // Pass 1: single tokens
  const single = new Set();
  norms.forEach((nm, i) => { if (nm && nm.includes(n)) single.add(i); });
  if (single.size) {
    return (
      <>
        {parts.map((p, i) => (single.has(i)
          ? <mark key={i} className="nr-hit">{p}</mark>
          : <React.Fragment key={i}>{p}</React.Fragment>))}
      </>
    );
  }

  // Pass 2: minimal runs of adjacent words (punctuation-only words like "-" ride along)
  const out = [];
  let i = 0;
  while (i < parts.length) {
    let end = -1;
    if (i % 2 === 0 && norms[i] && n.length >= 6) {
      let acc = norms[i];
      let words = 1;
      for (let j = i + 2; j < parts.length && words < HIGHLIGHT_MAX_RUN; j += 2) {
        acc += norms[j];
        words += 1;
        if (acc.length >= n.length + 8) break;
        if (acc.includes(n)) { end = j; break; }
      }
    }
    if (end >= 0) {
      out.push(<mark key={i} className="nr-hit">{parts.slice(i, end + 1).join('')}</mark>);
      i = end + 1;
    } else {
      out.push(<React.Fragment key={i}>{parts[i]}</React.Fragment>);
      i += 1;
    }
  }
  return <>{out}</>;
}

export function pluralize(n, word) {
  return `${n} ${word}${n === 1 ? '' : 's'}`;
}

// Debounce a fast-changing input value (search boxes) before it hits a query key
export function useDebounced(value, ms = 300) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}
